import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { getReadableTypeName } from '../../../utils/PlaceTypeMapping';
import '../../../styles/components/itinerary/saved_places/SearchBar.css';

const SearchBar = ({ savedPlaces, selectedItinerary, onSearchTermChange }) => {
  const searchInputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [typeSuggestions, setTypeSuggestions] = useState([]);
  const [allTypes, setAllTypes] = useState([]);

  const debouncedSearchTermChange = useCallback(
    debounce((value) => onSearchTermChange(value), 300),
    [onSearchTermChange]
  );

  useEffect(() => {
    if (!selectedItinerary) return;
    const itineraryPlaces = savedPlaces?.[selectedItinerary.id] ?? [];
    const allTypesSet = new Set();
    itineraryPlaces.forEach(place => {
      place.types?.forEach(type => {
        const readableType = getReadableTypeName(type) || '';
        if (readableType) {
          allTypesSet.add(type);
        }
      });
    });
    setAllTypes(Array.from(allTypesSet));
  }, [savedPlaces, selectedItinerary]);

  useEffect(() => {
    if (inputValue === '') {
      setNameSuggestions([]);
      setTypeSuggestions([]);
      debouncedSearchTermChange('');
      return;
    }
    const itineraryPlaces = savedPlaces?.[selectedItinerary?.id] ?? [];
    const filteredNameSuggestions = itineraryPlaces
      .filter(place => (place?.name || '').toLowerCase().includes(inputValue.toLowerCase()))
      .slice(0, 3);
    const filteredTypeSuggestions = allTypes
      .filter(type => {
        const readable = getReadableTypeName(type) || '';
        return readable.toLowerCase().includes(inputValue.toLowerCase());
      })
      .slice(0, 3);
    setNameSuggestions(filteredNameSuggestions);
    setTypeSuggestions(filteredTypeSuggestions);
    debouncedSearchTermChange(inputValue);
  }, [savedPlaces, selectedItinerary, inputValue, debouncedSearchTermChange, allTypes]);

  const handleSuggestionClick = (suggestion) => {
    const value = suggestion?.name || suggestion || '';
    setInputValue(value);
    onSearchTermChange(value);
    searchInputRef.current?.focus();
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    if (searchInputRef.current) {
      searchInputRef.current.setSelectionRange(0, searchInputRef.current.value.length);
    }
  };

  const handleResetClick = () => {
    setInputValue('');
    onSearchTermChange('');
    setNameSuggestions([]);
    setTypeSuggestions([]);
    setIsSearchFocused(false);
    searchInputRef.current?.focus();
  };

  return (
    <div className="saved-places-search-container">
      <div className="saved-places-search-box">
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search Saved Places..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className={`saved-places-search-input ${isSearchFocused ? 'expanded' : ''}`}
          onFocus={handleSearchFocus}
          onBlur={() => setIsSearchFocused(false)}
        />
        {inputValue && (
          <button className="saved-places-reset-button" onClick={handleResetClick}>✖</button>
        )}
      </div>
      {isSearchFocused && inputValue && (nameSuggestions.length > 0 || typeSuggestions.length > 0) && (
        <div className="saved-places-suggestions-wrapper">
          {nameSuggestions.length > 0 && (
            <>
              <div className="saved-places-suggestions-title">Places</div>
              <ul className="saved-places-suggestions-list">
                {nameSuggestions.map((suggestion, index) => (
                  <li
                    key={index}
                    className="saved-places-suggestion-item"
                    onMouseDown={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion?.name || ''}
                  </li>
                ))}
              </ul>
            </>
          )}
          {typeSuggestions.length > 0 && (
            <>
              <div className="saved-places-suggestions-title">Types</div>
              <ul className="saved-places-suggestions-list">
                {typeSuggestions.map((suggestion, index) => {
                  const readable = getReadableTypeName(suggestion) || '';
                  return (
                    <li
                      key={index}
                      className="saved-places-suggestion-item"
                      onMouseDown={() => handleSuggestionClick(readable)}
                    >
                      {readable}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

SearchBar.propTypes = {
  savedPlaces: PropTypes.object.isRequired,
  selectedItinerary: PropTypes.object,
  onSearchTermChange: PropTypes.func.isRequired,
};

export default SearchBar;
